/**
 * Strategy Service
 * Handles CRUD operations for strategies via the backend API.
 * Falls back to localStorage when offline or unauthenticated.
 */

import { getClerkInstance } from '../auth/clerkAuth';

export function strategyStorageKey(prefix) {
    const owner=getClerkInstance()?.user?.id;
    return owner ? prefix + ':' + encodeURIComponent(owner) : null;
}

const API_BASE = process.env.VUE_APP_API_BASE || '';

/**
 * Get auth token from Clerk
 */
async function getToken() {
    const auth = getClerkInstance();
    if (!auth || !auth.clerkInstance || !auth.clerkInstance.session) return null;
    try {
        return await auth.clerkInstance.session.getToken();
    } catch (e) {
        if(e.accountChanged)throw e;
        return null;
    }
}

/**
 * Make authenticated API request
 */
async function apiRequest(method, path, body = null) {
    const owner=getClerkInstance()?.user?.id;
    const unchanged=()=>{if(owner!==getClerkInstance()?.user?.id)throw Object.assign(new Error('Account changed. Try again.'),{accountChanged:true});};
    const token = await getToken();
    unchanged();
    if (!token) throw new Error('Not authenticated');

    const opts = {
        method,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    };
    if (body) opts.body = JSON.stringify(body);

    try {
    const res = await fetch(`${API_BASE}/api${path}`, opts);
    const data = await res.json();
    unchanged();
    if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
    return data;
    } catch(error) { unchanged(); throw error; }
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════

/**
 * List all strategies for the current user
 * @returns {Promise<Array>}
 */
export async function listStrategies() {
    try {
        const data = await apiRequest('GET', '/strategies');
        return data.strategies || [];
    } catch (e) {
        if(e.accountChanged)throw e;
        console.warn('[StrategyService] API unavailable, using localStorage fallback');
        return listStrategiesLocal();
    }
}

/**
 * List available templates
 * @returns {Promise<Array>}
 */
export async function listTemplates() {
    try {
        const data = await apiRequest('GET', '/strategies/templates');
        return data.templates || [];
    } catch (e) {
        if(e.accountChanged)throw e;
        return getBuiltInTemplates();
    }
}

/**
 * Get a single strategy by ID
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getStrategy(id) {
    try {
        const data = await apiRequest('GET', `/strategies/${id}`);
        return data.strategy;
    } catch (e) {
        if(e.accountChanged)throw e;
        // Fallback to localStorage
        return getStrategyLocal(id);
    }
}

/**
 * Create a new strategy
 * @param {Object} strategyData
 * @returns {Promise<string>} The new strategy ID
 */
export async function createStrategy(strategyData) {
    try {
        const data = await apiRequest('POST', '/strategies', strategyData);
        return data.id;
    } catch (e) {
        if(e.accountChanged)throw e;
        // Fallback to localStorage
        return createStrategyLocal(strategyData);
    }
}

/**
 * Update an existing strategy
 * @param {string} id
 * @param {Object} strategyData
 * @returns {Promise<boolean>}
 */
export async function updateStrategy(id, strategyData) {
    try {
        await apiRequest('PUT', `/strategies/${id}`, strategyData);
        return true;
    } catch (e) {
        if(e.accountChanged)throw e;
        // Fallback to localStorage
        return updateStrategyLocal(id, strategyData);
    }
}

/**
 * Delete a strategy
 * @param {string} id
 * @returns {Promise<boolean>}
 */
export async function deleteStrategy(id) {
    try {
        await apiRequest('DELETE', `/strategies/${id}`);
        return true;
    } catch (e) {
        if(e.accountChanged)throw e;
        return deleteStrategyLocal(id);
    }
}

/**
 * Duplicate a strategy
 * @param {string} id
 * @returns {Promise<string>} The new strategy ID
 */
export async function duplicateStrategy(id) {
    try {
        const data = await apiRequest('POST', `/strategies/${id}/duplicate`);
        return data.id;
    } catch (e) {
        if(e.accountChanged)throw e;
        const original = await getStrategy(id);
        if (!original) throw new Error('Strategy not found');
        return createStrategy({ ...original, name: (original.name || 'Strategy') + ' (Copy)', id: undefined });
    }
}

// ═══════════════════════════════════════════════════════════════
// LOCAL STORAGE FALLBACK
// ═══════════════════════════════════════════════════════════════

const LOCAL_KEY = 'candlescript_strategies';

function getLocalStrategies() {
    try {
        const key=strategyStorageKey(LOCAL_KEY);
        if(!key)return [];
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        if(e.accountChanged)throw e;
        return [];
    }
}

function saveLocalStrategies(strategies) {
    const key=strategyStorageKey(LOCAL_KEY);
    if(!key)throw new Error('Sign in to save a personal strategy.');
    localStorage.setItem(key, JSON.stringify(strategies));
}

function listStrategiesLocal() {
    return getLocalStrategies().map(s => ({
        id: s.id,
        name: s.name,
        description: s.description,
        symbol: s.symbol,
        timeframe: s.timeframe,
        tags: s.tags || [],
        isTemplate: false,
        updatedAt: s.updatedAt,
        createdAt: s.createdAt,
    }));
}

function getStrategyLocal(id) {
    const all = getLocalStrategies();
    return all.find(s => s.id === id) || null;
}

function createStrategyLocal(data) {
    const all = getLocalStrategies();
    const id = 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
    const strategy = { ...data, id, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    all.unshift(strategy);
    saveLocalStrategies(all);
    return id;
}

function updateStrategyLocal(id, data) {
    const all = getLocalStrategies();
    const idx = all.findIndex(s => s.id === id);
    if (idx < 0) return false;
    all[idx] = { ...all[idx], ...data, updatedAt: new Date().toISOString() };
    saveLocalStrategies(all);
    return true;
}

function deleteStrategyLocal(id) {
    const all = getLocalStrategies();
    const filtered = all.filter(s => s.id !== id);
    if (filtered.length === all.length) return false;
    saveLocalStrategies(filtered);
    return true;
}

// ═══════════════════════════════════════════════════════════════
// BUILT-IN TEMPLATES
// ═══════════════════════════════════════════════════════════════

export function getBuiltInTemplates() {
    return [
        {
            id: 'tpl_breakout_volume',
            name: 'Breakout + Volume',
            description: 'Enters on price breakout with volume confirmation. Uses ATR-based stops.',
            tags: ['intraday', 'momentum'],
            config: {
                marketIntent: 'intraday',
                marketContext: {
                    trendFilter: { enabled: true, type: 'SMA', period: 50, direction: 'Up' },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 14, condition: '> Avg' },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)' },
                },
                entryConditions: [
                    { id: 1, type: 'breakout', label: 'Breakout of High (20)', params: { period: 20 } },
                    { id: 2, type: 'volume_spike', label: 'Volume Spike x1.5', params: { multiplier: 1.5 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.5, label: 'ATR(14) × 1.5' },
                    takeProfit: { type: 'rrr', ratio: 2.0, label: 'RRR 2.0' },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1, tradeDirection: 'long' },
            },
        },
        {
            id: 'tpl_ma_crossover',
            name: 'Moving Average Crossover',
            description: 'Classic EMA 9/21 crossover strategy with trend filter.',
            tags: ['swing', 'trend'],
            config: {
                marketIntent: 'swing',
                marketContext: {
                    trendFilter: { enabled: true, type: 'SMA', period: 200, direction: 'Up' },
                    volatilityFilter: { enabled: false, type: 'ATR', period: 14, condition: '> Avg' },
                    volumeFilter: { enabled: false, type: 'Volume', period: 20, condition: '> Avg(20)' },
                },
                entryConditions: [
                    { id: 1, type: 'ma_crossover', label: 'EMA 9/21 Crossover', params: { fastPeriod: 9, slowPeriod: 21, maType: 'EMA' } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 2.0, label: 'ATR(14) × 2.0' },
                    takeProfit: { type: 'rrr', ratio: 3.0, label: 'RRR 3.0' },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 2, tradeDirection: 'both' },
            },
        },
        {
            id: 'tpl_rsi_reversal',
            name: 'RSI Mean Reversion',
            description: 'Buys oversold RSI bounces with volume confirmation.',
            tags: ['swing', 'mean-reversion'],
            config: {
                marketIntent: 'swing',
                marketContext: {
                    trendFilter: { enabled: false, type: 'SMA', period: 50, direction: 'Up' },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 14, condition: '> Avg' },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)' },
                },
                entryConditions: [
                    { id: 1, type: 'rsi_oversold', label: 'RSI < 30', params: { period: 14, level: 30 } },
                    { id: 2, type: 'volume_above_avg', label: 'Volume Above Avg', params: { period: 20, multiplier: 1.0 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'percent', label: '3% Stop', params: { percent: 3 } },
                    takeProfit: { type: 'rsi_exit', label: 'RSI > 70 Exit', params: { period: 14, exitLevel: 70 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1.5, tradeDirection: 'long' },
            },
        },
        {
            id: 'tpl_macd_momentum',
            name: 'MACD Momentum',
            description: 'Trades MACD crossovers with trend and volume filters.',
            tags: ['intraday', 'momentum'],
            config: {
                marketIntent: 'intraday',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 50, direction: 'Up' },
                    volatilityFilter: { enabled: false, type: 'ATR', period: 14, condition: '> Avg' },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)' },
                },
                entryConditions: [
                    { id: 1, type: 'macd_crossover', label: 'MACD Bullish Cross', params: { fast: 12, slow: 26, signal: 9 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.5, label: 'ATR(14) × 1.5' },
                    takeProfit: { type: 'rrr', ratio: 2.0, label: 'RRR 2.0' },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1, tradeDirection: 'both' },
            },
        },
        {
            id: 'tpl_bollinger_squeeze',
            name: 'Bollinger Squeeze Breakout',
            description: 'Enters when Bollinger Bands squeeze resolves with a breakout.',
            tags: ['positional', 'volatility'],
            config: {
                marketIntent: 'positional',
                marketContext: {
                    trendFilter: { enabled: true, type: 'SMA', period: 200, direction: 'Up' },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 20, condition: '> Avg' },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)' },
                },
                entryConditions: [
                    { id: 1, type: 'bb_squeeze', label: 'BB Squeeze', params: { period: 20, mult: 2, threshold: 0.5 } },
                    { id: 2, type: 'bb_breakout_upper', label: 'BB Upper Breakout', params: { period: 20, mult: 2 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 3.0, label: 'ATR(20) × 3.0' },
                    takeProfit: { type: 'rrr', ratio: 4.0, label: 'RRR 4.0' },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 2, tradeDirection: 'long' },
            },
        },
        {
            id: 'tpl_vt_confidence_trend',
            name: 'VT Confidence Trend',
            description: 'Uses VT Target signals only when trend, momentum, volume, volatility, and confidence alignment agree.',
            tags: ['VT', 'trend', 'confidence', 'long-short'],
            config: {
                marketIntent: 'swing',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 50, direction: 'Price Above', params: { period: 50 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 14, condition: '> Avg', params: { period: 14 } },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)', params: { period: 20 } },
                    rsiQualityFilter: { enabled: true, type: 'RSI', label: 'RSI Momentum Quality', period: 14, params: { period: 14, min: 42, max: 72 } },
                },
                indicators: [
                    { key: 'vt_target_main', type: 'VisualTrading', label: 'VT Target', category: 'Visual Trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
                    { key: 'ema_trend_50', type: 'EMA', label: 'EMA Trend 50', category: 'Moving Average', params: { period: 50 } },
                    { key: 'rsi_momentum_14', type: 'RSI', label: 'RSI Momentum 14', category: 'Momentum', params: { period: 14, level: 55 } },
                    { key: 'macd_momentum', type: 'MACD', label: 'MACD Momentum', category: 'Momentum', params: { fast: 12, slow: 26, signal: 9 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_momentum_confirmation', label: 'VT + RSI Confirmation (Long)', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, rsiPeriod: 14, rsiMinBull: 55, rsiMaxBear: 45 } },
                    { id: 2, type: 'vt_with_momentum_confirmation', label: 'VT + RSI Confirmation (Short)', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, rsiPeriod: 14, rsiMinBull: 55, rsiMaxBear: 45 } },
                    { id: 3, type: 'confidence_gate', label: 'Confidence >= 75', params: { minConfidence: 75, fastTrend: 21, slowTrend: 50, rsiPeriod: 14, rsiBull: 55, rsiBear: 45, volumeMultiplier: 1.05 } },
                    { id: 4, type: 'atr_expansion', label: 'Momentum Volatility Expansion', params: { period: 14, multiplier: 1.15 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.7, label: 'ATR(14) x 1.7', params: { period: 14, multiplier: 1.7 } },
                    takeProfit: { type: 'rrr', ratio: 2.6, label: 'RRR 2.6', params: { ratio: 2.6 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 5, maxTradesPerDay: 4, pyramiding: 0 },
            },
        },
        {
            id: 'tpl_vt_laser_scalper',
            name: 'VT Laser Momentum Scalper',
            description: 'Fast VT Laser setup for scalping, gated by short trend, momentum, confidence, and volume impulse.',
            tags: ['VT Laser', 'scalping', 'momentum', 'confidence'],
            config: {
                marketIntent: 'intraday',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 21, direction: 'Price Above', params: { period: 21 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 10, condition: '> Avg', params: { period: 10 } },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)', params: { period: 20 } },
                },
                indicators: [
                    { key: 'vt_laser_scalp', type: 'VTLaser', label: 'VT Laser Target', category: 'Visual Trading', params: { smaLength: 34, dcLength: 10, riskRewardRatio: 1.5 } },
                    { key: 'ema_scalp_21', type: 'EMA', label: 'EMA 21', category: 'Moving Average', params: { period: 21 } },
                    { key: 'rsi_scalp_7', type: 'RSI', label: 'RSI 7', category: 'Momentum', params: { period: 7, level: 55 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_volume_confirmation', label: 'VT Laser + Volume Confirmation (Long)', params: { smaLength: 34, dcLength: 10, riskRewardRatio: 1.5, volumePeriod: 20, volumeMultiplier: 1.3 } },
                    { id: 2, type: 'vt_with_volume_confirmation', label: 'VT Laser + Volume Confirmation (Short)', params: { smaLength: 34, dcLength: 10, riskRewardRatio: 1.5, volumePeriod: 20, volumeMultiplier: 1.3 } },
                    { id: 3, type: 'confidence_gate', label: 'Scalp Confidence >= 70', params: { minConfidence: 70, fastTrend: 9, slowTrend: 21, rsiPeriod: 7, rsiBull: 56, rsiBear: 44, volumeMultiplier: 1.2 } },
                    { id: 4, type: 'volume_spike', label: 'Volume Impulse x1.2', params: { multiplier: 1.2 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.1, label: 'ATR(10) x 1.1', params: { period: 10, multiplier: 1.1 } },
                    takeProfit: { type: 'rrr', ratio: 1.6, label: 'RRR 1.6', params: { ratio: 1.6 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 0.5, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 3, maxTradesPerDay: 8, pyramiding: 0 },
            },
        },
        {
            id: 'tpl_vt_multi_confirmation',
            name: 'VT Multi-Confirmation Swing',
            description: 'Combines VT Target with EMA trend, RSI range, MACD momentum, ATR expansion, and volume confirmation.',
            tags: ['VT', 'swing', 'multi-confirmation'],
            config: {
                marketIntent: 'swing',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 100, direction: 'Price Above', params: { period: 100 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 14, condition: '> Avg', params: { period: 14 } },
                    volumeFilter: { enabled: true, type: 'Volume', period: 30, condition: '> Avg(30)', params: { period: 30 } },
                    rsiMomentumFilter: { enabled: true, type: 'RSI', label: 'RSI Trend Band', period: 14, params: { period: 14, min: 45, max: 75 } },
                    adxProxyFilter: { enabled: true, type: 'ADX', label: 'Trend Strength Proxy', period: 14, params: { period: 14, minStrength: 25 } },
                },
                indicators: [
                    { key: 'vt_target_swing', type: 'VisualTrading', label: 'VT Target', category: 'Visual Trading', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3 } },
                    { key: 'ema_100_swing', type: 'EMA', label: 'EMA 100', category: 'Moving Average', params: { period: 100 } },
                    { key: 'bb_width_swing', type: 'BB', label: 'Bollinger Volatility', category: 'Volatility', params: { period: 20, mult: 2 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_trend_confirmation', label: 'VT + Trend Confirmation (Long)', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, confirmTrendPeriod: 100, requireTrendDirection: 'above' } },
                    { id: 2, type: 'vt_with_trend_confirmation', label: 'VT + Trend Confirmation (Short)', params: { smaLength: 50, dcLength: 20, riskRewardRatio: 3, confirmTrendPeriod: 100, requireTrendDirection: 'below' } },
                    { id: 3, type: 'macd_above_zero', label: 'MACD Momentum Bias', params: { fast: 12, slow: 26, signal: 9 } },
                    { id: 4, type: 'confidence_gate', label: 'Confidence >= 80', params: { minConfidence: 80, fastTrend: 34, slowTrend: 100, rsiPeriod: 14, rsiBull: 57, rsiBear: 43, volumeMultiplier: 1.0 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 2.1, label: 'ATR(14) x 2.1', params: { period: 14, multiplier: 2.1 } },
                    takeProfit: { type: 'rrr', ratio: 3.2, label: 'RRR 3.2', params: { ratio: 3.2 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1.25, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 8, maxTradesPerDay: 3, pyramiding: 0 },
            },
        },
        {
            id: 'tpl_vt_laser_breakout',
            name: 'VT Laser Breakout Continuation',
            description: 'Laser entries after Donchian breakout with confidence and volatility expansion filters.',
            tags: ['VT Laser', 'breakout', 'intraday'],
            config: {
                marketIntent: 'intraday',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 34, direction: 'Price Above', params: { period: 34 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 10, condition: '> Avg', params: { period: 10 } },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)', params: { period: 20 } },
                },
                indicators: [
                    { key: 'vt_laser_breakout', type: 'VTLaser', label: 'VT Laser Target', category: 'Visual Trading', params: { smaLength: 34, dcLength: 12, riskRewardRatio: 2 } },
                    { key: 'donchian_20', type: 'DC', label: 'Donchian 20', category: 'Channels', params: { period: 20 } },
                    { key: 'ema_34_breakout', type: 'EMA', label: 'EMA 34', category: 'Moving Average', params: { period: 34 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_momentum_confirmation', label: 'VT Laser + RSI Confirmation (Long)', params: { smaLength: 34, dcLength: 12, riskRewardRatio: 2, rsiPeriod: 10, rsiMinBull: 55, rsiMaxBear: 45 } },
                    { id: 2, type: 'vt_with_momentum_confirmation', label: 'VT Laser + RSI Confirmation (Short)', params: { smaLength: 34, dcLength: 12, riskRewardRatio: 2, rsiPeriod: 10, rsiMinBull: 55, rsiMaxBear: 45 } },
                    { id: 3, type: 'new_high', label: 'Fresh Breakout Structure', params: { period: 20 } },
                    { id: 4, type: 'confidence_gate', label: 'Confidence >= 72', params: { minConfidence: 72, fastTrend: 13, slowTrend: 34, rsiPeriod: 10, rsiBull: 55, rsiBear: 45, volumeMultiplier: 1.15 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.25, label: 'ATR(10) x 1.25', params: { period: 10, multiplier: 1.25 } },
                    takeProfit: { type: 'rrr', ratio: 2.1, label: 'RRR 2.1', params: { ratio: 2.1 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 0.75, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 4, maxTradesPerDay: 6, pyramiding: 0 },
            },
        },
        {
            id: 'tpl_vt_positional_regime',
            name: 'VT Positional Regime Filter',
            description: 'Higher timeframe-style VT template that only accepts signals during strong trend regimes.',
            tags: ['VT', 'positional', 'regime'],
            config: {
                marketIntent: 'positional',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'SMA', period: 200, direction: 'Price Above', params: { period: 200 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 20, condition: '> Avg', params: { period: 20 } },
                    volumeFilter: { enabled: false, type: 'Volume', period: 50, condition: '> Avg(50)', params: { period: 50 } },
                    rsiRegimeFilter: { enabled: true, type: 'RSI', label: 'RSI Regime', period: 14, params: { period: 14, min: 48, max: 78 } },
                },
                indicators: [
                    { key: 'vt_target_position', type: 'VisualTrading', label: 'VT Target', category: 'Visual Trading', params: { smaLength: 100, dcLength: 55, riskRewardRatio: 4 } },
                    { key: 'sma_200_regime', type: 'SMA', label: 'SMA 200', category: 'Moving Average', params: { period: 200 } },
                    { key: 'ema_50_regime', type: 'EMA', label: 'EMA 50', category: 'Moving Average', params: { period: 50 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_trend_confirmation', label: 'VT + Regime Confirmation (Long)', params: { smaLength: 100, dcLength: 55, riskRewardRatio: 4, confirmTrendPeriod: 200, requireTrendDirection: 'above' } },
                    { id: 2, type: 'vt_with_trend_confirmation', label: 'VT + Regime Confirmation (Short)', params: { smaLength: 100, dcLength: 55, riskRewardRatio: 4, confirmTrendPeriod: 200, requireTrendDirection: 'below' } },
                    { id: 3, type: 'confidence_gate', label: 'Regime Confidence >= 85', params: { minConfidence: 85, fastTrend: 50, slowTrend: 200, rsiPeriod: 14, rsiBull: 58, rsiBear: 42, volumeMultiplier: 0.8 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 3.0, label: 'ATR(20) x 3.0', params: { period: 20, multiplier: 3 } },
                    takeProfit: { type: 'rrr', ratio: 4.0, label: 'RRR 4.0', params: { ratio: 4 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 1.5, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 20, maxTradesPerDay: 1, pyramiding: 0 },
            },
        },
        {
            id: 'tpl_vt_laser_reversal_guarded',
            name: 'VT Laser Guarded Reversal',
            description: 'Contrarian laser setup guarded by confidence, RSI band, volatility expansion, and strict ATR risk.',
            tags: ['VT Laser', 'reversal', 'risk-controlled'],
            config: {
                marketIntent: 'intraday',
                conditionOperator: 'AND',
                marketContext: {
                    trendFilter: { enabled: true, type: 'EMA', period: 50, direction: 'Price Above', params: { period: 50 } },
                    volatilityFilter: { enabled: true, type: 'ATR', period: 14, condition: '> Avg', params: { period: 14 } },
                    volumeFilter: { enabled: true, type: 'Volume', period: 20, condition: '> Avg(20)', params: { period: 20 } },
                    rsiReversalBand: { enabled: true, type: 'RSI', label: 'RSI Reversal Band', period: 14, params: { period: 14, min: 35, max: 65 } },
                },
                indicators: [
                    { key: 'vt_laser_guarded', type: 'VTLaser', label: 'VT Laser Target', category: 'Visual Trading', params: { smaLength: 50, dcLength: 14, riskRewardRatio: 2 } },
                    { key: 'atr_guard', type: 'ATR', label: 'ATR Guard', category: 'Volatility', params: { period: 14 } },
                    { key: 'rsi_guard', type: 'RSI', label: 'RSI Guard', category: 'Momentum', params: { period: 14, level: 50 } },
                ],
                entryConditions: [
                    { id: 1, type: 'vt_with_volume_confirmation', label: 'VT Laser + Volume Guard (Long)', params: { smaLength: 50, dcLength: 14, riskRewardRatio: 2, volumePeriod: 20, volumeMultiplier: 1.4 } },
                    { id: 2, type: 'vt_with_volume_confirmation', label: 'VT Laser + Volume Guard (Short)', params: { smaLength: 50, dcLength: 14, riskRewardRatio: 2, volumePeriod: 20, volumeMultiplier: 1.4 } },
                    { id: 3, type: 'rsi_crossover', label: 'RSI Regains 50', params: { period: 14, level: 50 } },
                    { id: 4, type: 'confidence_gate', label: 'Confidence >= 68', params: { minConfidence: 68, fastTrend: 21, slowTrend: 50, rsiPeriod: 14, rsiBull: 52, rsiBear: 48, volumeMultiplier: 1.1 } },
                ],
                exitConditions: {
                    stopLoss: { type: 'atr', multiplier: 1.4, label: 'ATR(14) x 1.4', params: { period: 14, multiplier: 1.4 } },
                    takeProfit: { type: 'rrr', ratio: 2.0, label: 'RRR 2.0', params: { ratio: 2 } },
                },
                riskSettings: { positionSize: 'fixed', riskPercent: 0.75, tradeDirection: 'both' },
                execution: { orderType: 'market', cooldownBars: 6, maxTradesPerDay: 5, pyramiding: 0 },
            },
        },
    ];
}

export default {
    strategyStorageKey,
    listStrategies,
    listTemplates,
    getStrategy,
    createStrategy,
    updateStrategy,
    deleteStrategy,
    duplicateStrategy,
    getBuiltInTemplates,
};
